import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Cloudflare Email Routing state + actions for one site (zone required). */
export function useEmailRouting(site) {
  const [overview, setOverview] = useState(null);
  const [destInfo, setDestInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!site.cloudflare_zone_id) return;
    let cancelled = false;
    (async () => {
      try {
        const [ov, dest] = await Promise.all([api.get(`/email/sites/${site.id}`), api.get(`/email/admin-destination`)]);
        if (!cancelled) { setOverview(ov.data); setDestInfo(dest.data); setErr(""); }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [site.id, site.cloudflare_zone_id, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  async function run(fn) {
    setBusy(true); setErr("");
    try { await fn(); refresh(); return true; }
    catch (e) { setErr(e.response?.data?.detail || e.message); return false; }
    finally { setBusy(false); }
  }

  return {
    overview, destInfo, err, setErr, busy, refresh,
    bootstrap: () => run(() => api.post(`/email/sites/${site.id}/bootstrap`)),
    addPrefix: (prefix) => (prefix ? run(() => api.post(`/email/sites/${site.id}/addresses`, { prefix })) : Promise.resolve(false)),
    remove: (ruleId) => run(() => api.delete(`/email/sites/${site.id}/addresses/${ruleId}`)),
    toggleCatchAll: (enabled) => run(() => api.put(`/email/sites/${site.id}/catch-all`, { enabled })),
  };
}
